import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import salmonImage from '@/assets/salmon-loader-4.svg';
import salmonSantaImage from '@/assets/salmon-loader-4-santa.svg';

import styles from './Spinner.module.scss';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
    /**
     * `sm` sits inside a button or a line of text and `md` stands on its own in a small area,
     * both as a ring in the current text colour. `lg` is the salmon mark, centred in the
     * width it is given, for a page or section that is loading.
     */
    size?: SpinnerSize;
    /** `lg` only: holds the height of a page, so the mark does not sit at the top edge. */
    fullScreen?: boolean;
    /** What is loading, for screen readers. Defaults to "Loading". */
    label?: string;
    className?: string;
}

function isDecember(): boolean {
    return new Date().getMonth() === 11;
}

/** The salmon swims in a Santa hat through December. */
function SalmonMark() {
    const santa = isDecember();

    return (
        <img
            className={classNames(styles.salmon, { [styles.santa]: santa })}
            src={santa ? salmonSantaImage : salmonImage}
            alt=""
            aria-hidden="true"
        />
    );
}

/**
 * The portal's one busy indicator. A status region named by `label`, so a screen reader
 * hears what is loading whichever size draws it.
 */
export function Spinner(props: SpinnerProps) {
    const { size = 'sm', fullScreen = false, label, className } = props;
    const { t } = useTranslation();
    const isMark = size === 'lg';

    return (
        <span
            role="status"
            className={classNames(
                styles.spinner,
                styles[size],
                { [styles.fullScreen]: isMark && fullScreen },
                className
            )}>
            {isMark ? <SalmonMark /> : <span className={styles.ring} aria-hidden="true" />}
            <span className={styles.label}>{label ?? t('atoms.spinner.loading')}</span>
        </span>
    );
}

export default Spinner;
